import { ArrowRight, CheckCircle2, ExternalLink, Fingerprint, Info, LayoutGrid, Layers3, LoaderCircle, Maximize2, ShieldCheck, WalletCards, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { isHtmlContentType } from "../../../shared/gallery-content";
import { useAppState } from "../../app-state";
import { LocalInscriptionDocument } from "../../components/InscriptionDocumentFrame";
import { ResilientInscriptionMedia } from "../../components/ResilientInscriptionMedia";
import { fetchUtxos, filterCardinalUtxos, requestMarketplaceFeeChallenge, requestMarketplaceFeeQuote } from "../../lib/api";
import { buildInscriptionIds, inscriptionDraftsAreHistoryEligible, saveLocalInscriptionHistory } from "../../lib/history";
import { createInscriptionPlan, inscriptionRareSatManifestForPlan, MIN_FEE_RATE, type InscriptionPlan } from "../../lib/inscriptions";
import { applyPixelArtRendering } from "../../lib/pixel-art";
import { RecoveryRequiredError } from "../../lib/recovery-api";
import type { GalleryItem, InscriptionDraft, SignedPackage } from "../../lib/types";
import { keepCardinalUtxos, signWalletOwnershipMessage } from "../../lib/wallets";
import { downloadSignedPackage, executeInscription, type InscriptionStage } from "../../lib/workflow";
import { FeePresets } from "../workspace/Experience";
import { InscriptionFeeSummary as FeeSummary } from "./InscriptionFeeSummary";
import { RareSatReview } from "./RareSatPicker";
import type { InscriptionReviewProps } from "./inscription-review";
const numberFormatter = new Intl.NumberFormat("en-GB");
function bytes(value: number) { return value < 1024 ? value + " B" : value < 1024 * 1024 ? (value / 1024).toFixed(1) + " KB" : (value / 1024 / 1024).toFixed(2) + " MB"; }
function inscriptionContentUrl(id: string) { return "https://ordinals.com/content/" + id; }
function Notice({ tone = "neutral", children }: { tone?: string; children: ReactNode }) { return <div className={"notice " + tone} role={tone === "error" ? "alert" : undefined}>{tone === "error" ? <Info size={17} /> : tone === "success" ? <CheckCircle2 size={17} /> : <ShieldCheck size={17} />}<span>{children}</span></div>; }

export function RateControl({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  const normalize = (next: number) => Math.max(MIN_FEE_RATE, Math.round(next * 100) / 100);
  const set = (next: number) => {
    const normalized = normalize(next);
    setDraft(String(normalized));
    onChange(normalized);
  };

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commitDraft = () => {
    const parsed = Number(draft);
    if (!draft.trim() || !Number.isFinite(parsed)) {
      setDraft(String(value));
      return;
    }
    set(parsed);
  };

  return (
    <label className="rate-control">
      <span>{label}</span>
      <div>
        <button type="button" aria-label={`Decrease ${label}`} onClick={() => set(value - (value <= 1 ? 0.1 : 1))}>−</button>
        <input
          type="text"
          inputMode="decimal"
          aria-label={label}
          value={draft}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          onChange={(event) => {
            const raw = event.target.value;
            if (!/^\d*(?:\.\d*)?$/.test(raw)) return;
            setDraft(raw);
            if (!raw || raw.endsWith(".")) return;
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= MIN_FEE_RATE) onChange(normalize(parsed));
          }}
        />
        <small>sat/vB</small>
        <button type="button" aria-label={`Increase ${label}`} onClick={() => set(value + (value < 1 ? 0.1 : 1))}>+</button>
      </div>
      {value < 1 ? <em>Fractional relay rate</em> : null}
    </label>
  );
}

function reviewTextPreview(draft: InscriptionDraft): string {
  if (!draft.body?.length || draft.contentEncoding || !draft.mimeType?.startsWith("text/")) return "";
  try {
    const value = new TextDecoder().decode(draft.body).trim();
    return value.length > 800 ? `${value.slice(0, 800)}…` : value;
  } catch {
    return "";
  }
}

function formattedMetadata(metadata: unknown): string {
  try {
    return JSON.stringify(metadata, null, 2) ?? String(metadata);
  } catch {
    return String(metadata);
  }
}

export function ReviewImage({ draft, onOpen }: { draft: InscriptionDraft; onOpen: (src: string, name: string) => void }) {
  const imageBytes = draft.previewBody ?? (!draft.contentEncoding ? draft.body : undefined);
  const delegatedImageUrl = draft.delegateId ? inscriptionContentUrl(draft.delegateId) : "";
  const [failed, setFailed] = useState(false);
  const previewUrl = useMemo(() => {
    const previewMimeType = draft.previewMimeType ?? draft.mimeType;
    if (draft.previewFile && previewMimeType?.startsWith("image/")) return URL.createObjectURL(draft.previewFile);
    if (!imageBytes?.length || !previewMimeType?.startsWith("image/")) return "";
    return URL.createObjectURL(new Blob([Uint8Array.from(imageBytes)], { type: previewMimeType }));
  }, [draft.mimeType, draft.previewFile, draft.previewMimeType, imageBytes]);
  const source = previewUrl || delegatedImageUrl;

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);
  useEffect(() => setFailed(false), [source]);

  if (!source || failed) return null;
  return (
    <button className="review-image-thumb" type="button" aria-label={`View ${draft.name}`} onClick={() => onOpen(source, draft.name)}>
      <img src={source} alt={`Preview of ${draft.name}`} onLoad={(event) => applyPixelArtRendering(event.currentTarget)} onError={() => setFailed(true)} />
      <span><Maximize2 /></span>
    </button>
  );
}

function ReviewParentPreview({ inscriptionId, index }: { inscriptionId: string; index: number }) {
  const role = index === 0 ? "PRIMARY" : `CO-PARENT ${index}`;
  const label = `${role} parent inscription preview`;
  return (
    <a className="review-parent-card" href={`https://ordinals.com/inscription/${inscriptionId}`} target="_blank" rel="noreferrer" title={inscriptionId}>
      <ReviewParentMedia inscriptionId={inscriptionId} label={label} priority={index === 0} />
      <span>
        <small>{role}</small>
        <strong>{inscriptionId}</strong>
      </span>
      <ExternalLink aria-hidden="true" />
    </a>
  );
}

function ReviewParentMedia({
  inscriptionId,
  label,
  priority,
}: {
  inscriptionId: string;
  label: string;
  priority: boolean;
}) {
  const [directImageFailed, setDirectImageFailed] = useState(false);

  useEffect(() => {
    setDirectImageFailed(false);
  }, [inscriptionId]);

  if (directImageFailed) {
    return <ResilientInscriptionMedia
      inscriptionId={inscriptionId}
      label={label}
      className="review-parent-media"
      priority={priority}
    />;
  }

  return (
    <span className="review-parent-media">
      <img
        src={inscriptionContentUrl(inscriptionId)}
        alt={label}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        onLoad={(event) => applyPixelArtRendering(event.currentTarget)}
        onError={() => setDirectImageFailed(true)}
      />
    </span>
  );
}

export function GalleryPreviewImage({ item }: { item: GalleryItem }) {
  return <ResilientInscriptionMedia inscriptionId={item.inscriptionId} label={item.attributes?.title ?? "Gallery item"} className="gallery-preview-image"><LayoutGrid /></ResilientInscriptionMedia>;
}

export function GalleryItemsPreview({ items, limit = 12 }: { items: GalleryItem[]; limit?: number }) {
  const visible = items.slice(0, limit);
  return (
    <div className="gallery-items-preview">
      <div className="gallery-preview-list">
        {visible.map((item, index) => {
          const traits = Object.entries(item.attributes?.traits ?? {});
          return (
            <a className="gallery-preview-row" href={`https://ordinals.com/inscription/${item.inscriptionId}`} target="_blank" rel="noreferrer" key={`${item.inscriptionId}-detail`}>
              <GalleryPreviewImage item={item} />
              <span className="gallery-preview-index">{String(index + 1).padStart(2, "0")}</span>
              <div className="gallery-preview-copy">
                <code title={item.inscriptionId}>{item.inscriptionId}</code>
                {item.attributes?.title ? <strong>{item.attributes.title}</strong> : null}
                {traits.length ? <div className="gallery-preview-traits">{traits.map(([name, value]) => <span key={name}><small>{name}</small><em>{value === null ? "Null" : String(value)}</em></span>)}</div> : <small className="gallery-preview-empty">NO ITEM METADATA</small>}
              </div>
              <ExternalLink />
            </a>
          );
        })}
        {items.length > limit ? <div className="gallery-preview-remaining">+{numberFormatter.format(items.length - limit)} MORE ITEMS</div> : null}
      </div>
    </div>
  );
}

export function SubmitPanel({ drafts, feeRate, onFeeRate, title, disabledReason, displayCount, prepareDrafts, preparationProgress, resolveRevealOptions, planDependency, returnPath = "/inscribe", completionPath = "/complete", recoveryPath = "/console", presentation }: InscriptionReviewProps) {
  const { authorizeWallet, config, wallet, setWalletModalOpen, setCompletion } = useAppState();
  const navigate = useNavigate();
  const [skipBroadcast, setSkipBroadcast] = useState(false);
  const [plan, setPlan] = useState<InscriptionPlan | null>(null);
  const [reviewDrafts, setReviewDrafts] = useState<InscriptionDraft[] | null>(null);
  const [feeAuthorization, setFeeAuthorization] = useState("");
  const [sessionAuthorization, setSessionAuthorization] = useState("");
  const [stage, setStage] = useState<InscriptionStage | "preparing" | "uploading-content" | null>(null);
  const [error, setError] = useState("");
  const [recoveryRequired, setRecoveryRequired] = useState<SignedPackage | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [previewing, setPreviewing] = useState<{ src: string; name: string } | null>(null);
  const activeDrafts = plan && reviewDrafts ? reviewDrafts : drafts;
  const itemCount = plan ? activeDrafts.length : displayCount ?? drafts.length;
  const contentBytes = activeDrafts.reduce((total, draft) => total + (draft.body?.length ?? 0), 0);
  const metadataCount = activeDrafts.filter((draft) => draft.metadata !== undefined || draft.attributes !== undefined || draft.gallery?.length).length;
  const busy = stage !== null;

  useEffect(() => {
    setPlan(null);
    setFeeAuthorization("");
    setSessionAuthorization("");
    setPreviewing(null);
  }, [drafts, feeRate, planDependency, wallet]);

  const prepare = async () => {
    if (recoveryRequired) return;
    setError("");
    if (disabledReason) return setError(disabledReason);
    if (!wallet) {
      setWalletModalOpen(true);
      return;
    }
    if (!config?.feeConfigured) return setError("The marketplace fee wallet must be configured before any real transaction can be prepared.");
    try {
      setStage(prepareDrafts ? "uploading-content" : "preparing");
      const nextDrafts = prepareDrafts ? await prepareDrafts() : drafts;
      if (!nextDrafts.length) throw new Error("Add something to inscribe first");
      setReviewDrafts(nextDrafts);
      setStage("preparing");
      const revealOptions = resolveRevealOptions ? await resolveRevealOptions(wallet) : undefined;
      let feeResult = await requestMarketplaceFeeQuote(
        wallet.payment.address,
        wallet.ordinals.address,
        nextDrafts.length,
        feeRate,
      );
      if (feeResult.ownershipRequired) {
        const challenge = await requestMarketplaceFeeChallenge(
          wallet.payment.address,
          wallet.ordinals.address,
          nextDrafts.length,
        );
        const paymentSignature = await signWalletOwnershipMessage(wallet, wallet.payment.address, challenge.message);
        const ordinalsSignature = wallet.payment.address.toLowerCase() === wallet.ordinals.address.toLowerCase()
          ? paymentSignature
          : await signWalletOwnershipMessage(wallet, wallet.ordinals.address, challenge.message);
        feeResult = await requestMarketplaceFeeQuote(
          wallet.payment.address,
          wallet.ordinals.address,
          nextDrafts.length,
          feeRate,
          { challengeToken: challenge.challengeToken, paymentSignature, ordinalsSignature },
        );
      }
      if (feeResult.ownershipRequired) throw new Error("Wallet ownership could not be verified");
      const feeQuote = feeResult.quote;
      const mempoolUtxos = await fetchUtxos(wallet.payment.address);
      const walletApprovedUtxos = await keepCardinalUtxos(wallet, mempoolUtxos);
      const cardinalUtxos = await filterCardinalUtxos(walletApprovedUtxos);
      const nextPlan = createInscriptionPlan({
        wallet,
        drafts: nextDrafts,
        utxos: cardinalUtxos,
        commitFeeRate: feeRate,
        revealFeeRate: feeRate,
        marketplaceFeeAddress: feeQuote.marketplaceFeeAddress,
        marketplaceFeeSats: feeQuote.marketplaceFeeSats,
        revealOptions,
      });
      const rareSatManifest = inscriptionRareSatManifestForPlan(nextPlan);
      let verifiedSession = "";
      if (rareSatManifest || nextPlan.parentIds.length) {
        verifiedSession = await authorizeWallet(true) ?? "";
        if (!verifiedSession) throw new Error("Verify both wallet addresses before using a selected sat or parent inscription.");
      }
      setPlan(nextPlan);
      setFeeAuthorization(feeQuote.authorization);
      setSessionAuthorization(verifiedSession);
      setReviewing(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not prepare this inscription");
    } finally {
      setStage(null);
    }
  };

  const sign = async () => {
    if (recoveryRequired || !wallet || !plan || !feeAuthorization || !reviewDrafts) return;
    setError("");
    try {
      const signedPackage = await executeInscription({
        plan,
        wallet,
        feeAuthorization,
        sessionAuthorization: sessionAuthorization || undefined,
        skipBroadcast,
        onStage: setStage,
      });
      const historyEligible = inscriptionDraftsAreHistoryEligible(reviewDrafts);
      if (skipBroadcast) downloadSignedPackage(signedPackage);
      else if (historyEligible) saveLocalInscriptionHistory(wallet.payment.address, {
        commitTxid: signedPackage.commitTxid,
        revealTxid: signedPackage.revealTxid,
        count: reviewDrafts.length,
        inscriptionIds: buildInscriptionIds(signedPackage.revealTxid, reviewDrafts.length),
        createdAt: Date.now(),
        confirmed: false,
        broadcastVerified: true,
      }, window.localStorage);
      setCompletion({ package: signedPackage, summary: plan.summary, name: title, count: reviewDrafts.length, broadcast: !skipBroadcast, returnPath });
      void navigate(completionPath);
    } catch (caught) {
      if (caught instanceof RecoveryRequiredError) { setRecoveryRequired(caught.saved); setReviewing(false); }
      setError(caught instanceof Error ? caught.message : "Signing was not completed");
      setStage(null);
    }
  };

  const stageLabel: Record<Exclude<typeof stage, null>, string> = {
    preparing: "CHECKING FUNDS",
    "uploading-content": preparationProgress === undefined ? "UPLOADING" : `UPLOADING ${preparationProgress}%`,
    "signing-commit": "APPROVE PAYMENT",
    "building-reveal": "BUILDING INSCRIPTION",
    "mining-vanity": "FINDING ID",
    "signing-reveal": "APPROVE INSCRIPTION",
    "broadcasting-commit": "SENDING PAYMENT",
    "broadcasting-reveal": "SENDING INSCRIPTION",
    "retrying-reveal": "PAYMENT RECEIVED · RETRYING INSCRIPTION",
    "verifying-commit": "CHECKING PAYMENT ACCEPTANCE",
    "verifying-reveal": "CHECKING INSCRIPTION ACCEPTANCE",
    "retrying-commit": "RETRYING SAVED TRANSACTION",
    complete: "DONE",
  };

  return (
    <aside className="checkout-card">
      <div className="checkout-heading"><p className="eyebrow">TOTAL // {itemCount} {itemCount === 1 ? "ITEM" : "ITEMS"}</p></div>
      <FeeSummary summary={plan?.summary} count={itemCount} contentBytes={contentBytes} feeRate={feeRate} />
      <div className="single-rate">
        <RateControl label="Fee rate" value={feeRate} onChange={onFeeRate} />
        <FeePresets onChange={onFeeRate} showGuidance={false} />
      </div>
      {feeRate < 1 ? <Notice tone="warning">SUB‑1 SAT/VB</Notice> : null}
      <label className="check-row">
        <input type="checkbox" checked={skipBroadcast} onChange={(event) => setSkipBroadcast(event.target.checked)} />
        <span><strong>Prepare only</strong><small>Sign and save a package. Nothing is sent to Bitcoin.</small></span>
      </label>
      {!config?.feeConfigured ? <Notice tone="warning">FEE WALLET NOT SET</Notice> : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      {stage === "retrying-reveal" ? <p role="status">Your payment is accepted. Retrying the saved inscription automatically. Keep this page open; do not pay again.</p> : null}
      {stage === "verifying-commit" || stage === "verifying-reveal" || stage === "retrying-commit" ? <p role="status">Checking Bitcoin acceptance of your saved transaction. Keep this page open; no replacement payment or signature is needed.</p> : null}
      {recoveryRequired ? <div className="completion-actions"><Link className="primary-button" to={recoveryPath}>TRANSACTION RECOVERY</Link><button className="secondary-button" type="button" onClick={() => downloadSignedPackage(recoveryRequired)}>DOWNLOAD SAVED PACKAGE</button></div> : null}
      <button className="primary-button wide" type="button" disabled={busy || Boolean(recoveryRequired) || itemCount === 0} onClick={() => void prepare()}>
        {busy ? <><LoaderCircle className="spin" /> {stage ? stageLabel[stage] : "PREPARING"}</> : wallet ? <><Fingerprint /> REVIEW & INSCRIBE <ArrowRight /></> : <><WalletCards /> CONNECT WALLET <ArrowRight /></>}
      </button>

      {reviewing && plan ? createPortal(
        <div className={presentation === "artefact" ? "review-overlay ar-inscriber ar-inscription-review" : "review-overlay"} role="presentation" onMouseDown={() => !busy && setReviewing(false)}>
          <section className="review-sheet inscription-review" role="dialog" aria-modal="true" aria-labelledby="review-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="review-content">
              <div className="modal-heading"><div><p className="eyebrow">CHECK BEFORE SIGNING</p><h2 id="review-title">What will be inscribed</h2></div><button className="icon-button" type="button" aria-label="Close review" disabled={busy} onClick={() => setReviewing(false)}><X /></button></div>
              <div className="review-overview">
                <div><span>INSCRIPTIONS</span><strong>{plan.summary.count}</strong></div>
                <div><span>METADATA</span><strong>{metadataCount ? `${metadataCount} ${metadataCount === 1 ? "ITEM" : "ITEMS"}` : "NONE"}</strong></div>
                <div><span>PARENTS</span><strong>{plan.parentIds.length || "NONE"}</strong></div>
                <div><span>SELECTED SATS</span><strong>{plan.sourceSources.length ? plan.sourceSources.length : "AUTOMATIC"}</strong></div>
              </div>

              {plan.parentIds.length ? (
                <div className="review-parent">
                  <span><Layers3 size={16} /> {plan.parentIds.length === 1 ? "PARENT INSCRIPTION" : `${plan.parentIds.length} PARENT INSCRIPTIONS`}</span>
                  <div className="review-parent-list">
                    {plan.parentIds.map((id, index) => <ReviewParentPreview inscriptionId={id} index={index} key={id} />)}
                  </div>
                  <small>Every item below will be linked to {plan.parentIds.length === 1 ? "this parent" : "all of these parents"}.</small>
                </div>
              ) : null}

              <div className="review-items" aria-label="Inscriptions to review">
                {activeDrafts.map((draft, index) => {
                  const traits = Object.entries(draft.attributes?.traits ?? {});
                  const textPreview = reviewTextPreview(draft);
                  const destination = plan.recipients[index] ?? "Unknown";
                  return (
                    <article className="review-item" key={draft.id}>
                      <header className={draft.previewFile || draft.mimeType?.startsWith("image/") || draft.delegateId ? "review-item-heading with-image" : "review-item-heading"}>
                        <ReviewImage draft={draft} onOpen={(src, name) => setPreviewing({ src, name })} />
                        <span className="review-item-number">{String(index + 1).padStart(2, "0")}</span>
                        <div className="review-item-copy">
                          <strong title={draft.name}>{draft.name}</strong>
                          <small>
                            {draft.sourceLabel ?? (draft.gallery?.length
                              ? draft.delegateId ? "GALLERY · INSCRIPTION IMAGE" : draft.mimeType?.startsWith("image/") ? "GALLERY · IMAGE" : isHtmlContentType(draft.mimeType ?? "") ? "GALLERY · HTML" : "GALLERY · TEXT"
                              : draft.delegateId ? "DELEGATE" : draft.mimeType ?? "FILE")}
                            {draft.sourceSize ? ` · ${bytes(draft.sourceSize)} SOURCE` : ""}
                            {draft.body?.length ? ` · ${bytes(draft.body.length)} ON-CHAIN` : ""}
                            {draft.contentEncoding ? " · BROTLI" : ""}
                          </small>
                        </div>
                        <RareSatReview source={plan.sourceSources[index]} />
                      </header>

                      <div className="review-destination">
                        <span>DESTINATION</span>
                        <strong>{destination}</strong>
                      </div>

                      {draft.delegateId ? (
                        <div className="review-data-block">
                          <span>{draft.gallery?.length ? "GALLERY IMAGE INSCRIPTION" : "DELEGATE INSCRIPTION"}</span>
                          <code>{draft.delegateId}</code>
                        </div>
                      ) : null}

                      {draft.gallery?.length && draft.body?.length && !draft.contentEncoding && isHtmlContentType(draft.mimeType ?? "") ? <div className="gallery-html-review"><LocalInscriptionDocument body={draft.body} mimeType={draft.mimeType!} title={`${draft.name} HTML preview`} /></div> : null}

                      {textPreview ? (
                        <div className="review-data-block">
                          <span>{isHtmlContentType(draft.mimeType ?? "") ? "HTML SOURCE" : "TEXT CONTENT"}</span>
                          <pre>{textPreview}</pre>
                        </div>
                      ) : null}

                      {draft.metadata !== undefined ? (
                        <div className="review-data-block">
                          <span>ON-CHAIN METADATA</span>
                          <pre>{formattedMetadata(draft.metadata)}</pre>
                        </div>
                      ) : null}

                      {draft.attributes?.title || traits.length ? (
                        <div className="review-properties">
                          <span>PROPERTIES</span>
                          {draft.attributes?.title ? <strong>{draft.attributes.title}</strong> : null}
                          {traits.length ? (
                            <div className="review-traits">
                              {traits.map(([name, value]) => <span key={name}><small>{name}</small><em>{value === null ? "Null" : String(value)}</em></span>)}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {draft.gallery?.length ? (
                        <div className="review-gallery-block">
                          <span><LayoutGrid /> GALLERY ITEMS · {numberFormatter.format(draft.gallery.length)}</span>
                          <small>The gallery points to these existing inscriptions. It does not move or take ownership of them.</small>
                          <GalleryItemsPreview items={draft.gallery} />
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="review-action">
              <FeeSummary summary={plan.summary} count={itemCount} contentBytes={contentBytes} feeRate={feeRate} />
              {stage ? <Notice tone="neutral"><LoaderCircle className="spin" /> {stageLabel[stage]}</Notice> : null}
              {error ? <Notice tone="error">{error}</Notice> : null}
              <p className="review-sign-note">{skipBroadcast ? "Prepare only is on. Your wallet will sign a saved package; no transactions will be sent to Bitcoin." : "Your wallet will ask you to approve these inscriptions."}</p>
              <button className="primary-button wide" type="button" disabled={busy || Boolean(recoveryRequired)} onClick={() => void sign()}>
                {busy ? <><LoaderCircle className="spin" /> {stage ? stageLabel[stage] : "WAITING"}</> : <><Fingerprint /> {skipBroadcast ? "SIGN & SAVE PACKAGE" : "SIGN & INSCRIBE"} <ArrowRight /></>}
              </button>
            </div>
          </section>
          {previewing ? (
            <div className="review-image-lightbox" role="dialog" aria-modal="true" aria-label={`Preview of ${previewing.name}`} onMouseDown={(event) => { event.stopPropagation(); setPreviewing(null); }}>
              <div onMouseDown={(event) => event.stopPropagation()}>
                <header><span><small>INSCRIPTION PREVIEW</small><strong>{previewing.name}</strong></span><button className="icon-button" type="button" aria-label="Close image preview" onClick={() => setPreviewing(null)}><X /></button></header>
                <img src={previewing.src} alt={`Full preview of ${previewing.name}`} onLoad={(event) => applyPixelArtRendering(event.currentTarget)} />
              </div>
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </aside>
  );
}

